/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {ChangeEvent, useEffect, useState} from 'react';
import {makeStyles} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import {
  CTCEntity
} from '@ion/core/src/types/entities';
import Dropdown from '@ion/dls/src/components/menus/Dropdown';
import {onHandleChange} from '@ion/dls/src/editors/ChooserUtils';

// defines the prop definitions for types option ...
export interface TypeOption {
  label: string;
  value: string;
  disabled?: boolean;
};

// defines the prop definitions from types chooser ...
export interface ContactTypeChooserProps {
  onChange?: (contact: CTCEntity, key: number) => void;
  fieldLabel?: string;
  defaultValue?: TypeOption['value'];
  readOnly?: boolean;
  required?: boolean;
  hasError?: boolean;
  keyObj: any;
};

// defines the styles used by types chooser ...
const useStyles = makeStyles(theme => ({
  root : {
    padding : '0px !important',
    margin  : '0px !important'
  }
}));

/**
 * Generates the types options list ...
 */
function getTypesOptions() {
  const typeOptions: TypeOption[] = [];
  const typemap = translate('contacts:phone.types', {returnObjects: true});
  Object.keys(typemap)?.forEach((key: string) => {
    typeOptions?.push({label: typemap[key], value: key});
  });
  return typeOptions;
};

/**
 * Allows user to choose a preferred types from one of the available/supported
 * types.
 *
 * @param onChange: Callback fired with the selected value when a
 *   selection is made
 * @param fieldLabel: field label displayed above input
 * @param hasError: f the field has an error the value is passed on
 * to the dropdown component
 * @param required: if ture, the field is mandatory with an asterisk sign
 * displayed next to the field label
 * @param defaultValue: Default value passed to the component.
 * Will only be considered if passed as TypeOption.
 * @param readOnly: If true then dropdown is disabled
 *
 * ```typescript
 * import {ContactTypeChooser} from '@ion/dls/src/editors/';
 *
 * <ContactTypeChooser options={options}
 *   fieldLabel={translate('common:types.fieldLabel')}
 *   defaultValue='default'
 *   hasError={true}
 *   readOnly={false}
 *   onChange={handleChange} />
 *
 * ```
 */
export const ContactTypeChooser = ({
  onChange,
  defaultValue,
  hasError,
  readOnly,
  keyObj,
  fieldLabel = translate('contacts:phone.type')
}: ContactTypeChooserProps) => {
  const classes                   = useStyles();
  const [selected, setSelected]   = useState<string>(defaultValue);
  const options: TypeOption[]     = getTypesOptions();

  useEffect(() => {
    const matchIndex = options?.findIndex(({value}: {value: string}) => value === defaultValue);
    const matchingOption = options?.[matchIndex];
    if (defaultValue) {
      setSelected(matchingOption?.label);
    }
  }, []);

  return (
    <div className={classes.root}>
      <Dropdown options={options}
        fieldLabel={fieldLabel}
        hasError={hasError}
        disabled={readOnly}
        value={selected}
        onChange={onHandleChange({setSelected, onChange, options}, keyObj)} />
    </div>
  );
  return null;
};

export default ContactTypeChooser;
